import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getThemeEffect,
  getDefaultPopupForTheme,
  isFestiveTheme,
  usesGhanaFlag,
  usesCoatOfArms,
  usesIndependenceWave,
  usesRamadanDecor
} from '../utils/siteTheme';

const PARTICLE_COUNT_DESKTOP = 48;
const PARTICLE_COUNT_MOBILE = 26;
const PARTICLE_COUNT_TABLET = 34;
const SNOWFLAKE_GLYPHS = ['❄', '❅', '❆', '✦'];

/** Non-snow themed accents (no ❄) — float gently. Ghana national days use the flag instead. */
const FLOAT_PARTICLES = {
  eid: { glyphs: ['☪', '✦', '★', '✧'], className: 'tgh-particle--eid' },
  mothers: { glyphs: ['♥', '♡', '❤', '✦'], className: 'tgh-particle--heart' },
  fathers: { glyphs: ['✦', '★', '✧'], className: 'tgh-particle--fathers' },
  africa: { glyphs: ['★', '✦', '✧'], className: 'tgh-particle--africa' },
  easter: { glyphs: ['❀', '❁', '✿', '✦'], className: 'tgh-particle--petal' },
  farmers: { glyphs: ['✦', '★', '❀'], className: 'tgh-particle--farmers' },
  boxing: { glyphs: ['✦', '★', '✧'], className: 'tgh-particle--sparkle' }
};

const VALENTINE_HEARTS = ['❤', '♥', '💕', '♡', '💖'];

function HeartRain({ count }) {
  const items = useMemo(
    () =>
      Array.from({ length: count }, (_, index) => {
        const size = 12 + ((index * 9) % 18);
        const left = (index * 13 + (index % 7) * 7) % 98;
        const delay = ((index * 0.37) % 16).toFixed(2);
        const duration = (10 + (index % 8) * 1.4).toFixed(2);
        const sway = (2.2 + (index % 5) * 0.5).toFixed(2);
        const drift = ((index % 2 === 0 ? 1 : -1) * (16 + (index % 8) * 7)).toFixed(0);
        const glyph = VALENTINE_HEARTS[index % VALENTINE_HEARTS.length];
        const tone = index % 4;
        return { id: index, size, left, delay, duration, sway, drift, glyph, tone };
      }),
    [count]
  );

  return (
    <div className="tgh-heart-rain" aria-hidden="true">
      {items.map((item) => (
        <span
          key={item.id}
          className={`tgh-heart-drop tgh-heart-drop--${item.tone}`}
          style={{
            left: `${item.left}%`,
            fontSize: `${item.size}px`,
            animationDelay: `${item.delay}s, ${item.delay}s`,
            animationDuration: `${item.duration}s, ${item.sway}s`,
            '--tgh-heart-drift': `${item.drift}px`
          }}
        >
          {item.glyph}
        </span>
      ))}
    </div>
  );
}

/** Detailed Moroccan-style fanous lantern (SVG). */
function RamadanLanternMark({ className = '', variant = 'a' }) {
  const glowId = `ramadan-glow-${variant}`;
  const metalId = `ramadan-metal-${variant}`;
  return (
    <svg className={className} viewBox="0 0 80 120" aria-hidden="true">
      <defs>
        <radialGradient id={glowId} cx="50%" cy="48%" r="45%">
          <stop offset="0%" stopColor="#ecfeff" stopOpacity="1" />
          <stop offset="45%" stopColor="#67e8f9" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#0284c7" stopOpacity="0.15" />
        </radialGradient>
        <linearGradient id={metalId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="45%" stopColor="#0ea5e9" />
          <stop offset="100%" stopColor="#0369a1" />
        </linearGradient>
      </defs>
      {/* Hook + top cap */}
      <path d="M40 6 C40 6 40 14 40 16" stroke="#7dd3fc" strokeWidth="2" fill="none" />
      <ellipse cx="40" cy="18" rx="12" ry="5" fill={`url(#${metalId})`} />
      <path d="M28 20 L52 20 L48 28 L32 28 Z" fill={`url(#${metalId})`} />
      {/* Body */}
      <path
        d="M26 28 H54 L58 36 L54 88 H26 L22 36 Z"
        fill={`url(#${metalId})`}
        stroke="#a5f3fc"
        strokeWidth="1.2"
      />
      {/* Filigree / lattice */}
      <path
        d="M32 40 H48 M32 50 H48 M32 60 H48 M32 70 H48 M32 80 H48"
        stroke="#e0f2fe"
        strokeWidth="1"
        opacity="0.55"
      />
      <path
        d="M34 36 V84 M40 36 V84 M46 36 V84"
        stroke="#bae6fd"
        strokeWidth="0.9"
        opacity="0.45"
      />
      {/* Inner light */}
      <ellipse cx="40" cy="58" rx="11" ry="16" fill={`url(#${glowId})`} className="tgh-ramadan-lamp-glow" />
      {/* Bottom finial */}
      <path d="M30 88 H50 L46 96 H34 Z" fill={`url(#${metalId})`} />
      <circle cx="40" cy="100" r="3.5" fill="#7dd3fc" />
      <circle cx="40" cy="106" r="2" fill="#38bdf8" opacity="0.85" />
    </svg>
  );
}

/** One crescent moon with a glowing star beside it. */
function RamadanMoonMark({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 110 96" aria-hidden="true">
      <defs>
        <radialGradient id="ramadan-crescent-fill" cx="32%" cy="34%" r="72%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="40%" stopColor="#e0f2fe" />
          <stop offset="100%" stopColor="#38bdf8" />
        </radialGradient>
        <radialGradient id="ramadan-side-star-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="55%" stopColor="#a5f3fc" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
        </radialGradient>
        {/* Mask punches a clean single crescent — no second silhouette */}
        <mask id="ramadan-crescent-mask" maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width="110" height="96" fill="#000" />
          <circle cx="46" cy="48" r="32" fill="#fff" />
          <circle cx="58" cy="42" r="24" fill="#000" />
        </mask>
      </defs>
      <circle
        className="tgh-ramadan-moon-body"
        cx="46"
        cy="48"
        r="32"
        fill="url(#ramadan-crescent-fill)"
        mask="url(#ramadan-crescent-mask)"
      />
      {/* Glowing star at the crescent’s side */}
      <circle cx="90" cy="34" r="14" fill="url(#ramadan-side-star-glow)" className="tgh-ramadan-moon-star-glow" />
      <polygon
        className="tgh-ramadan-moon-star"
        fill="#f8fafc"
        points="90,22 92.6,30.2 101.2,30.2 94.4,35.2 96.8,43.4 90,38.4 83.2,43.4 85.6,35.2 78.8,30.2 87.4,30.2"
      />
    </svg>
  );
}

/** Ramadan: professional hanging lanterns, crescent moon & glowing stars. */
function RamadanDecor({ starCount, compact = false }) {
  const stars = useMemo(
    () =>
      Array.from({ length: starCount }, (_, index) => ({
        id: index,
        left: (index * 17 + (index % 5) * 11) % 96,
        top: (index * 23 + (index % 7) * 9) % 88,
        size: compact ? 1.5 + (index % 3) : 2 + (index % 4),
        delay: ((index * 0.33) % 4).toFixed(2),
        duration: (1.8 + (index % 5) * 0.5).toFixed(2)
      })),
    [starCount, compact]
  );

  const hangings = useMemo(() => {
    if (compact) {
      /* Phone: moon + 2 lanterns — no edge clipping */
      return [
        { id: 'lantern-a', type: 'lantern', left: 14, size: 0.78, delay: '0s', variant: 'a' },
        { id: 'moon-main', type: 'moon', left: 48, size: 0.95, delay: '0.15s' },
        { id: 'lantern-c', type: 'lantern', left: 78, size: 0.82, delay: '0.4s', variant: 'c' }
      ];
    }
    return [
      { id: 'lantern-a', type: 'lantern', left: 10, size: 0.92, delay: '0s', variant: 'a' },
      { id: 'lantern-b', type: 'lantern', left: 28, size: 1.08, delay: '0.35s', variant: 'b' },
      { id: 'moon-main', type: 'moon', left: 52, size: 1.2, delay: '0.15s' },
      { id: 'lantern-c', type: 'lantern', left: 74, size: 1, delay: '0.55s', variant: 'c' },
      { id: 'lantern-d', type: 'lantern', left: 88, size: 0.82, delay: '0.25s', variant: 'd' }
    ];
  }, [compact]);

  return (
    <div className={`tgh-ramadan${compact ? ' tgh-ramadan--compact' : ''}`} aria-hidden="true">
      <div className="tgh-ramadan-stars">
        {stars.map((star) => (
          <span
            key={star.id}
            className="tgh-ramadan-glow-star"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`
            }}
          />
        ))}
      </div>
      <div className="tgh-ramadan-hangings">
        {hangings.map((item) => (
          <div
            key={item.id}
            className={`tgh-ramadan-hang tgh-ramadan-hang--${item.type}`}
            style={{
              left: `${item.left}%`,
              '--tgh-ramadan-scale': item.size,
              animationDelay: item.delay
            }}
          >
            <span className="tgh-ramadan-string" />
            {item.type === 'lantern' ? (
              <RamadanLanternMark
                className="tgh-ramadan-icon tgh-ramadan-icon--lantern"
                variant={item.variant || 'a'}
              />
            ) : (
              <RamadanMoonMark className="tgh-ramadan-icon tgh-ramadan-icon--moon" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function buildParticles(count, glyphs) {
  return Array.from({ length: count }, (_, index) => {
    const size = 10 + ((index * 11) % 16);
    const left = (index * 19 + (index % 7) * 5) % 100;
    const delay = ((index * 0.41) % 14).toFixed(2);
    const duration = (11 + (index % 9) * 1.55).toFixed(2);
    const sway = (2.4 + (index % 5) * 0.55).toFixed(2);
    const drift = ((index % 2 === 0 ? 1 : -1) * (18 + (index % 7) * 6)).toFixed(0);
    const spin = (8 + (index % 6) * 3).toFixed(1);
    const tone = index % 4;
    const glyph = glyphs[index % glyphs.length];
    return { id: index, size, left, delay, duration, sway, drift, spin, tone, glyph };
  });
}

/** Official Ghana flag: Red / Gold / Green with Black star on the gold band. */
function GhanaFlagMark({ className = '' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 900 600"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Flag of Ghana"
    >
      <rect width="900" height="200" y="0" fill="#CE1126" />
      <rect width="900" height="200" y="200" fill="#FCD116" />
      <rect width="900" height="200" y="400" fill="#006B3F" />
      <polygon
        fill="#111111"
        points="450,245 466,292 516,292 476,322 491,370 450,340 409,370 424,322 384,292 434,292"
      />
    </svg>
  );
}

function SmallGhanaFlags({ count, rain = false }) {
  const compact =
    typeof window !== 'undefined' && window.innerWidth <= 767.98;
  const items = useMemo(() => {
    if (!rain) {
      return buildParticles(count, ['flag']).map((item) => ({
        ...item,
        mode: 'float'
      }));
    }
    return Array.from({ length: count }, (_, index) => {
      const size = (compact ? 12 : 16) + ((index * 7) % (compact ? 10 : 14));
      const left = (index * 15 + (index % 6) * 8) % 94;
      const delay = ((index * 0.48) % 14).toFixed(2);
      const duration = (11 + (index % 7) * 1.5).toFixed(1);
      const sway = (2.6 + (index % 4) * 0.55).toFixed(2);
      const drift = ((index % 2 === 0 ? 1 : -1) * (18 + (index % 6) * 7)).toFixed(0);
      return { id: index, size, left, delay, duration, sway, drift, mode: 'rain' };
    });
  }, [count, rain, compact]);

  return (
    <div
      className={`tgh-float-field tgh-ghana-flag-field${rain ? ' tgh-ghana-flag-rain' : ''}`}
      aria-hidden="true"
    >
      {items.map((item) => (
        <span
          key={item.id}
          className={
            rain ? 'tgh-ghana-flag-drop' : 'tgh-float-particle tgh-ghana-flag-mini'
          }
          style={
            rain
              ? {
                  left: `${item.left}%`,
                  width: `${item.size}px`,
                  animationDelay: `${item.delay}s, ${item.delay}s`,
                  animationDuration: `${item.duration}s, ${item.sway}s`,
                  '--tgh-flag-drift': `${item.drift}px`
                }
              : {
                  left: `${item.left}%`,
                  top: `${8 + (item.id % 75)}%`,
                  width: `${18 + (item.id % 10)}px`,
                  animationDelay: `${item.delay}s`,
                  animationDuration: `${(9 + (item.id % 7)).toFixed(1)}s`
                }
          }
        >
          <GhanaFlagMark className="tgh-ghana-flag-svg" />
        </span>
      ))}
    </div>
  );
}

/** Independence Day: full-bleed page backdrop (behind UI) + subtle flag rain. */
function IndependenceDecor({ flagCount }) {
  useEffect(() => {
    document.documentElement.classList.add('tgh-independence-active');
    return () => {
      document.documentElement.classList.remove('tgh-independence-active');
    };
  }, []);

  const pageBg =
    typeof document !== 'undefined'
      ? createPortal(
          <div className="tgh-independence-page-bg" aria-hidden="true">
            <img
              src="/img/festive/independence-arch.png"
              alt=""
              className="tgh-independence-arch"
              draggable="false"
            />
            <div className="tgh-independence-scrim" />
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {pageBg}
      <div className="tgh-independence" aria-hidden="true">
        <SmallGhanaFlags count={flagCount} rain />
      </div>
    </>
  );
}

/** Official coat of arms of Ghana (national emblem asset). */
function GhanaCoatOfArmsMark({ className = '' }) {
  return (
    <img
      className={className}
      src="/img/festive/ghana-coat-of-arms.png"
      alt=""
      draggable="false"
      loading="lazy"
      decoding="async"
    />
  );
}

function SmallCoatOfArms({ count }) {
  const items = useMemo(
    () =>
      Array.from({ length: count }, (_, index) => {
        const size = 28 + ((index * 7) % 16);
        const left = (index * 17 + (index % 5) * 9) % 92;
        const delay = ((index * 0.85) % 12).toFixed(2);
        const duration = (14 + (index % 6) * 2.2).toFixed(1);
        const sway = (3.2 + (index % 4) * 0.7).toFixed(2);
        const drift = ((index % 2 === 0 ? 1 : -1) * (22 + (index % 6) * 8)).toFixed(0);
        return { id: index, size, left, delay, duration, sway, drift };
      }),
    [count]
  );

  return (
    <div className="tgh-float-field tgh-coat-arms-field" aria-hidden="true">
      {items.map((item) => (
        <span
          key={item.id}
          className="tgh-coat-arms-mini"
          style={{
            left: `${item.left}%`,
            width: `${item.size}px`,
            animationDelay: `${item.delay}s, ${item.delay}s`,
            animationDuration: `${item.duration}s, ${item.sway}s`,
            '--tgh-coat-drift': `${item.drift}px`
          }}
        >
          <GhanaCoatOfArmsMark className="tgh-coat-arms-img" />
        </span>
      ))}
    </div>
  );
}

function FireworkCanvas({ active, palette, intensity = 'normal', burstShape = 'circle' }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!active) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    let particles = [];
    let rockets = [];
    let lastBurst = 0;
    const blast = intensity === 'blast';
    const heartBurst = burstShape === 'heart';
    const colors = palette || [
      '#ff4d6d',
      '#ffd166',
      '#06d6a0',
      '#4cc9f0',
      '#f72585',
      '#ffffff',
      '#c77dff'
    ];

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * Math.min(window.devicePixelRatio || 1, 2);
      canvas.height = height * Math.min(window.devicePixelRatio || 1, 2);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(canvas.width / width, 0, 0, canvas.height / height, 0, 0);
    };

    const heartPoint = (t) => {
      const x = 16 * Math.sin(t) ** 3;
      const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
      return { x, y };
    };

    const burst = (x, y) => {
      if (heartBurst) {
        const outline = 72 + Math.floor(Math.random() * 24);
        const scale = 0.22 + Math.random() * 0.08;
        for (let i = 0; i < outline; i += 1) {
          const t = (Math.PI * 2 * i) / outline;
          const pt = heartPoint(t);
          const speed = scale * (0.85 + Math.random() * 0.35);
          particles.push({
            x,
            y,
            vx: pt.x * speed,
            vy: pt.y * speed,
            life: 1,
            decay: 0.008 + Math.random() * 0.01,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: 1.8 + Math.random() * 2.4,
            drag: 0.985,
            gravity: 0.012
          });
        }
        /* Soft fill sparkle inside the heart after the outline forms */
        for (let i = 0; i < 36; i += 1) {
          const t = Math.random() * Math.PI * 2;
          const pt = heartPoint(t);
          const fill = 0.08 + Math.random() * 0.14;
          particles.push({
            x,
            y,
            vx: pt.x * fill,
            vy: pt.y * fill,
            life: 1,
            decay: 0.014 + Math.random() * 0.016,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: 1.2 + Math.random() * 1.8,
            drag: 0.99,
            gravity: 0.02
          });
        }
        return;
      }

      const count = (blast ? 58 : 42) + Math.floor(Math.random() * (blast ? 40 : 28));
      for (let i = 0; i < count; i += 1) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.2;
        const speed = (blast ? 2.1 : 1.6) + Math.random() * (blast ? 4.6 : 3.8);
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          decay: 0.01 + Math.random() * (blast ? 0.016 : 0.018),
          color: colors[Math.floor(Math.random() * colors.length)],
          size: (blast ? 1.8 : 1.4) + Math.random() * (blast ? 2.8 : 2.2),
          drag: 0.99,
          gravity: 0.028
        });
      }
    };

    const launchRocket = () => {
      rockets.push({
        x: width * (0.12 + Math.random() * 0.76),
        y: height + 10,
        vx: (Math.random() - 0.5) * (blast || heartBurst ? 1.6 : 1.2),
        vy: -(blast || heartBurst ? 8.2 : 7.5) - Math.random() * (blast || heartBurst ? 4.2 : 3.5),
        color: colors[Math.floor(Math.random() * colors.length)]
      });
    };

    const tick = (time) => {
      ctx.clearRect(0, 0, width, height);

      const gap = heartBurst
        ? 1600 + Math.random() * 1200
        : blast
          ? 1100 + Math.random() * 900
          : 2200 + Math.random() * 1600;
      if (time - lastBurst > gap) {
        launchRocket();
        if (Math.random() > (blast || heartBurst ? 0.3 : 0.45)) launchRocket();
        if (blast && Math.random() > 0.55) launchRocket();
        lastBurst = time;
      }

      rockets = rockets.filter((rocket) => {
        rocket.x += rocket.vx;
        rocket.y += rocket.vy;
        rocket.vy += 0.045;
        ctx.beginPath();
        ctx.fillStyle = rocket.color;
        ctx.arc(rocket.x, rocket.y, blast || heartBurst ? 2.6 : 2.2, 0, Math.PI * 2);
        ctx.fill();
        if (rocket.vy >= -1.2 || rocket.y < height * (0.18 + Math.random() * 0.25)) {
          burst(rocket.x, rocket.y);
          return false;
        }
        return rocket.y > -20;
      });

      particles = particles.filter((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity ?? 0.028;
        p.vx *= p.drag ?? 0.99;
        p.vy *= p.drag ?? 0.99;
        p.life -= p.decay;
        if (p.life <= 0) return false;
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.beginPath();
        ctx.fillStyle = p.color;
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      rafRef.current = window.requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener('resize', resize);
    lastBurst = performance.now() - (blast || heartBurst ? 800 : 1800);
    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', resize);
      window.cancelAnimationFrame(rafRef.current);
      ctx.clearRect(0, 0, width, height);
    };
  }, [active, palette, intensity, burstShape]);

  return <canvas ref={canvasRef} className="tgh-firework-canvas" aria-hidden="true" />;
}

function AnimatedSantaSleigh() {
  return (
    <div className="tgh-santa-figure">
      <img
        src="/img/festive/santa-sleigh.png"
        alt=""
        className="tgh-santa-img"
        draggable="false"
      />
    </div>
  );
}

function readFestiveStateFromDom() {
  const root = document.documentElement;
  const themeId = root.getAttribute('data-tgh-theme') || '';
  const effectAttr = root.getAttribute('data-tgh-effect');
  const effect = effectAttr || getThemeEffect(themeId);
  const festive = isFestiveTheme(themeId) || (effect && effect !== 'none');
  const popupEnabled = root.getAttribute('data-tgh-popup-enabled') === '1';
  const popupMessage =
    root.getAttribute('data-tgh-popup-message') || getDefaultPopupForTheme(themeId) || '';
  return {
    themeId,
    effect: festive ? effect : 'none',
    festive,
    enabled: festive || (popupEnabled && String(popupMessage).trim()),
    popupEnabled,
    popupMessage: String(popupMessage).trim()
  };
}

function popupStorageKey(themeId) {
  return `tgh-holiday-popup-closed:${themeId || 'default'}`;
}

function particleCountForViewport() {
  if (typeof window === 'undefined') return PARTICLE_COUNT_DESKTOP;
  const w = window.innerWidth;
  if (w <= 575.98) return PARTICLE_COUNT_MOBILE;
  if (w <= 991.98) return PARTICLE_COUNT_TABLET;
  return PARTICLE_COUNT_DESKTOP;
}

function coatOfArmsCountForViewport() {
  if (typeof window === 'undefined') return 10;
  const w = window.innerWidth;
  if (w <= 575.98) return 6;
  if (w <= 991.98) return 8;
  return 10;
}

/**
 * Holiday festive layer: snow only for Christmas / New Year’s Eve;
 * other holidays use light float accents + closable themed popup.
 */
function FestiveEffects() {
  const [state, setState] = useState(() => readFestiveStateFromDom());
  const [particleCount, setParticleCount] = useState(PARTICLE_COUNT_DESKTOP);
  const [coatCount, setCoatCount] = useState(10);
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767.98
  );
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const syncTheme = () => setState(readFestiveStateFromDom());
    const syncViewport = () => {
      setParticleCount(particleCountForViewport());
      setCoatCount(coatOfArmsCountForViewport());
      setIsCompact(window.innerWidth <= 767.98);
    };

    syncTheme();
    syncViewport();

    const onTheme = () => syncTheme();
    window.addEventListener('tgh-theme-updated', onTheme);

    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [
        'data-tgh-theme',
        'data-tgh-effect',
        'data-tgh-popup-enabled',
        'data-tgh-popup-message'
      ]
    });

    window.addEventListener('resize', syncViewport);
    return () => {
      window.removeEventListener('tgh-theme-updated', onTheme);
      window.removeEventListener('resize', syncViewport);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!state.enabled || !state.popupEnabled || !state.popupMessage) {
      setShowPopup(false);
      return;
    }

    const key = popupStorageKey(state.themeId);
    try {
      if (sessionStorage.getItem(key) === '1') {
        setShowPopup(false);
        return;
      }
    } catch (_error) {
      // private mode — still show
    }

    setShowPopup(true);
  }, [state.enabled, state.popupEnabled, state.popupMessage, state.themeId]);

  const closePopup = () => {
    setShowPopup(false);
    try {
      sessionStorage.setItem(popupStorageKey(state.themeId), '1');
    } catch (_error) {
      // ignore
    }
  };

  /* Snow only for Christmas and New Year’s Eve / Day */
  const showSnow =
    state.festive && (state.effect === 'christmas' || state.effect === 'newyear');
  const showSanta = state.festive && state.effect === 'christmas';
  const showValentine = state.festive && state.effect === 'valentine';
  const showRamadan = state.festive && usesRamadanDecor(state.effect);
  const showFireworks = showSnow || showValentine;
  const showGhanaFlag = state.festive && usesGhanaFlag(state.effect);
  const showIndependenceWave = state.festive && usesIndependenceWave(state.effect);
  const showCoatOfArms = state.festive && usesCoatOfArms(state.effect);
  const floatConfig =
    state.festive &&
    !showSnow &&
    !showGhanaFlag &&
    !showIndependenceWave &&
    !showCoatOfArms &&
    !showValentine &&
    !showRamadan
      ? FLOAT_PARTICLES[state.effect] || null
      : null;
  const showFloat = Boolean(floatConfig);

  const independenceFlagCount = useMemo(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const factor = w <= 575.98 ? 0.45 : w <= 767.98 ? 0.6 : 0.95;
    return Math.max(isCompact ? 8 : 16, Math.floor(particleCount * factor));
  }, [particleCount, isCompact]);

  const snowParticles = useMemo(
    () => (showSnow ? buildParticles(particleCount, SNOWFLAKE_GLYPHS) : []),
    [showSnow, particleCount]
  );

  const floatParticles = useMemo(
    () =>
      floatConfig
        ? buildParticles(Math.max(12, Math.floor(particleCount * 0.55)), floatConfig.glyphs)
        : [],
    [floatConfig, particleCount]
  );

  const heartRainCount = Math.max(
    28,
    Math.floor(particleCount * (typeof window !== 'undefined' && window.innerWidth <= 575.98 ? 1.1 : 1.35))
  );

  const fireworkPalette = showValentine
    ? ['#ff1744', '#ff4d6d', '#f72585', '#ff8fab', '#ffffff', '#ffd6e0', '#c9184a']
    : ['#ff4d6d', '#ffd166', '#06d6a0', '#4cc9f0', '#f72585', '#ffffff', '#c77dff'];

  if (!state.enabled) return null;

  return (
    <div className={`tgh-festive tgh-festive--${state.effect}`}>
      {showFireworks && (
        <FireworkCanvas
          active
          palette={fireworkPalette}
          intensity={state.effect === 'newyear' ? 'blast' : 'normal'}
          burstShape={showValentine ? 'heart' : 'circle'}
        />
      )}

      {showSnow && (
        <div className="tgh-snowfall" aria-hidden="true">
          {snowParticles.map((item) => (
            <span
              key={item.id}
              className={`tgh-snowflake tgh-snowflake--${item.tone} tgh-particle--snow`}
              style={{
                left: `${item.left}%`,
                fontSize: `${item.size}px`,
                animationDelay: `${item.delay}s, ${item.delay}s`,
                animationDuration: `${item.duration}s, ${item.sway}s`,
                '--tgh-snow-drift': `${item.drift}px`,
                '--tgh-snow-spin': `${item.spin}s`
              }}
            >
              {item.glyph}
            </span>
          ))}
        </div>
      )}

      {showValentine && <HeartRain count={heartRainCount} />}

      {showRamadan && (
        <RamadanDecor
          compact={isCompact}
          starCount={Math.max(
            isCompact ? 14 : 28,
            Math.floor(particleCount * (isCompact ? 0.55 : 1.15))
          )}
        />
      )}

      {showIndependenceWave && (
        <IndependenceDecor flagCount={independenceFlagCount} />
      )}

      {showGhanaFlag && (
        <SmallGhanaFlags count={Math.max(14, Math.floor(particleCount * 0.65))} />
      )}

      {showCoatOfArms && <SmallCoatOfArms count={coatCount} />}

      {showFloat && (
        <div className="tgh-float-field" aria-hidden="true">
          {floatParticles.map((item) => (
            <span
              key={item.id}
              className={`tgh-float-particle ${floatConfig.className}`}
              style={{
                left: `${item.left}%`,
                top: `${12 + (item.id % 70)}%`,
                fontSize: `${item.size}px`,
                animationDelay: `${item.delay}s`,
                animationDuration: `${(10 + (item.id % 8)).toFixed(1)}s`
              }}
            >
              {item.glyph}
            </span>
          ))}
        </div>
      )}

      {showPopup && state.popupMessage && (
        <div
          className="tgh-holiday-popup is-visible"
          role="dialog"
          aria-modal="false"
          aria-label="Holiday message"
        >
          <div className="tgh-holiday-popup-card">
            <button
              type="button"
              className="tgh-holiday-popup-close"
              onClick={closePopup}
              aria-label="Close message"
            >
              ×
            </button>
            <span className="tgh-holiday-popup-eyebrow">Tema General Hospital</span>
            <p className="tgh-holiday-popup-message">{state.popupMessage}</p>
          </div>
        </div>
      )}

      {showSanta && (
        <div className="tgh-santa-ride" aria-hidden="true">
          <div className="tgh-santa-track">
            <AnimatedSantaSleigh />
          </div>
        </div>
      )}
    </div>
  );
}

export default FestiveEffects;
